import React, { useEffect, useRef, useState } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import ReactDOM from 'react-dom';
import { MoreVertical, Plus, Edit3, Trash2 } from 'lucide-react';

interface SubcategoryMenuProps {
  subcategoryId: string;
  categoryName: string;
  subcategoryName: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onAddItem: () => void;
  onRename: (newName: string) => void;
  onDelete: () => void;
}

export const SubcategoryMenu: React.FC<SubcategoryMenuProps> = ({
  subcategoryId,
  categoryName,
  subcategoryName,
  isOpen,
  onToggle,
  onClose,
  onAddItem,
  onRename,
  onDelete}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      // Calculate menu position when opened
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.right - 192 // 192px is the width of the menu (w-48)
        });
      }
      
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  const handleRename = () => {
    const newName = prompt(`Change name for "${subcategoryName}"`, subcategoryName);
    if (newName && newName.trim() && newName !== subcategoryName) {
      const trimmedName = newName.trim();
      
      // Validate the subcategory name
      if (trimmedName.toLowerCase() === 'items') {
        alert('"Items" is a reserved name. Please choose a different name.');
        return;
      }
      
      if (trimmedName.length < 2) {
        alert('Subcategory name must be at least 2 characters long.');
        return;
      }
      
      if (trimmedName.length > 50) {
        alert('Subcategory name must be less than 50 characters.');
        return;
      }
      
      onRename(trimmedName);
    }
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm(
      `Are you sure you want to delete the subcategory "${subcategoryName}"?\n\n` +
      `All items in this subcategory will be moved directly under "${categoryName}".`
    )) {
      onDelete();
    }
    onClose();
  };

  return (
    <div style={styles.table.actionMenuWrapper}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={styles.table.actionButton}
        title="Subcategory Actions"
        type="button"
      >
        <MoreVertical size={16} style={styles.table.actionIcon} />
      </button>
      
      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            ...styles.table.menuContainer,
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddItem();
              onClose();
            }}
            style={styles.table.menuButton}
          >
            <Plus size={16} style={styles.table.menuButtonIcon} />
            Add item
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRename();
            }}
            style={styles.table.menuButton}
          >
            <Edit3 size={16} style={styles.table.menuButtonIcon} />
            Change name
          </button>
          
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            style={styles.table.menuButton}
          >
            <Trash2 size={16} style={{ color: '#ef4444' }} />
            Delete subcategory
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};